import { isPlatformBrowser } from '@angular/common';
import { ErrorHandler, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/compat/messaging';
import { SwPush } from '@angular/service-worker';
import type { AppState } from '@capacitor/app';
import { App } from '@capacitor/app';
import type { ActionPerformed as LocalNotificationActionPerformed } from '@capacitor/local-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import type {
  ActionPerformed as PushNotificationActionPerformed,
  Channel,
  PermissionStatus,
  PushNotificationSchema,
  Token,
} from '@capacitor/push-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { Auth } from '@freelancer/auth';
import { FreelancerHttp } from '@freelancer/freelancer-http';
import { LocalStorage } from '@freelancer/local-storage';
import { Location } from '@freelancer/location';
import { Pwa } from '@freelancer/pwa';
import { Tracking } from '@freelancer/tracking';
import type { QTSExtraParams } from '@freelancer/tracking/interface';
import { firstValueFrom, toNumber } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

export type NotificationPermission = 'granted' | 'denied' | 'default';

export type NotificationRegisterResult =
  | {
      readonly status: 'success';
    }
  | {
      readonly status: 'error';
      readonly errorCode: NotificationPermissionErrorCode;
      // T249142: Required to capture what the problem we're having with
      // some users not registering push notifications correctly.
      readonly errorMsg?: string;
    };

export enum NotificationPermissionErrorCode {
  //  Permission has been denied by the user
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  TOKEN_REQUEST_ERROR = 'TOKEN_REQUEST_ERROR',
}

interface Notification {
  type: 'push' | 'local';
  data?: {
    url?: string;
    resource_id?: string;
    push_notification_type?: string;
  };
}

export type PushNotificationQtsExtraParams = Partial<
  Pick<
    QTSExtraParams,
    'pushNotificationType' | 'url' | 'resourceId' | 'clicked'
  >
>;

const TRACK_CLICKED_NOTIF_EVENT_NAME = 'clicked_notification';
const TRACK_DELIVERED_NOTIF_EVENT_NAME = 'delivered_notification';
const TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME = 'native_push_notifications';

@Injectable({ providedIn: 'root' })
export class Notifications {
  tokenChanges$: Rx.Observable<string | undefined>;

  private subscriptions = new Rx.Subscription();
  private isRegistered = false;

  constructor(
    private afMessaging: AngularFireMessaging,
    private auth: Auth,
    private errorHandler: ErrorHandler,
    private freelancerHttp: FreelancerHttp,
    private localStorage: LocalStorage,
    private location: Location,
    private push: SwPush,
    private pwa: Pwa,
    private tracking: Tracking,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  /**
   * Can permission to show notifications be requested. This implies that
   * permission has not been already granded.
   *
   * Used that to display a user prompt to enable notifications
   */
  async canRequestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }
    const permission = await this.getPermission();
    return permission === 'default';
  }

  /**
   * Has permission to show notifications already been granted
   *
   * Used to automatically register push notifications on login
   */
  async hasGrantedPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      return false;
    }
    const permission = await this.getPermission();
    return permission === 'granted';
  }

  /**
   * Are notifications supported by the platform.
   *
   * If this is true but both canRequestPermission() are hasGrantedPermission()
   * are false, this implies the user has previously denied permission: use
   * that a display a user prompt to try to recover the user.
   */
  isSupported(): boolean {
    // Disable on the server
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    // Notifications are always supported on native
    if (this.pwa.isNative()) {
      return true;
    }

    // In the browser, both Service Workers & the Push API need to be supported
    // Technically we don't use the Angular Service Worker but using
    // `SwPush::isEnabled` here allows to share the SW enabling logic.
    return (
      this.push.isEnabled && 'PushManager' in window && 'Notification' in window
    );
  }

  /**
   * Register the application to receive push notifications. Will request
   * permission to the user if needed, i.e. do not call that on load unless
   * permission has already been granted.
   */
  async registerPushNotifications(): Promise<NotificationRegisterResult> {
    if (!this.isSupported()) {
      throw new Error(
        'Push notifications are not supported on the current platform: make sure to check isSupported() before calling registerPushNotifications()',
      );
    }

    if (!this.auth.isLoggedIn()) {
      throw new Error('User must be logged-in to register push notifications');
    }

    if (
      !(await this.hasGrantedPermission()) &&
      !(await this.canRequestPermission())
    ) {
      return {
        status: 'error',
        errorCode: NotificationPermissionErrorCode.PERMISSION_DENIED,
      };
    }

    // Do nothing if already registered
    if (this.isRegistered) {
      return {
        status: 'success',
      };
    }

    let foregroundMessages$: Rx.Observable<object>;
    // Foreground messages stream specifically for native.
    let nativeForegroundMessages$:
      | Rx.Observable<PushNotificationSchema>
      | undefined;
    let notificationClicks$: Rx.Observable<{
      action?: string;
      notification: Notification;
    }>;

    if (this.pwa.isNative()) {
      // Notification channel is for Android only. Android notifications have to
      // be bound to a channel in order for it to display with alert/toast style
      // and other configurations including lights/vibration.
      if (this.pwa.getPlatform() === 'android') {
        const newMessageChannel: Channel = {
          // This channel ID is also used as the default channel for firebase
          // messaging notifications, set via
          // `com.google.firebase.messaging.default_notification_channel_id`
          id: 'newMessageChannel',
          name: $localize`New Message`,
          description: $localize`New incoming messages`,
          importance: 5,
          sound: 'message.wav',
          visibility: 1,
          vibration: true,
          lights: true,
          lightColor: '#29B2FE',
        };
        PushNotifications.createChannel(newMessageChannel);
        LocalNotifications.createChannel(newMessageChannel);
      }

      // We need to use a replay subject here to cache the registration result until tokenChanges$ is subcribed to
      const registrationSubject$ = new Rx.ReplaySubject<string | undefined>(1);
      PushNotifications.addListener('registration', (t: Token) => {
        this.removeNotificationsOnActiveState();

        // Set listener to clear notifications everytime user switches back to the app
        App.addListener('appStateChange', async (state: AppState) => {
          if (state.isActive) {
            await this.trackDeliveredNotifications();
            PushNotifications.removeAllDeliveredNotifications();
          }
        });

        // Clear the notifications when the user lands on the inbox.
        this.subscriptions.add(
          this.location
            .valueChanges()
            .pipe(filter(location => location.pathname.startsWith('/messages')))
            .subscribe(_ => {
              this.removeNotificationsOnActiveState();
            }),
        );
        registrationSubject$.next(t.value);
      });
      PushNotifications.addListener('registrationError', (t: any) =>
        registrationSubject$.error(t.data),
      );

      this.tokenChanges$ = Rx.from(
        PushNotifications.requestPermissions()
          .then((res: PermissionStatus) => {
            if (res.receive === 'granted') {
              return PushNotifications.register();
            }
            registrationSubject$.error(
              new Error('Permission denied by the user'),
            );
          })
          .catch(() =>
            registrationSubject$.error(
              new Error('Permission denied by user after being granted'),
            ),
          ),
      ).pipe(switchMap(() => registrationSubject$.asObservable()));

      nativeForegroundMessages$ = new Rx.Observable(observer => {
        const listener = PushNotifications.addListener(
          'pushNotificationReceived',
          (notification: PushNotificationSchema) => observer.next(notification),
        );
        return () => listener.remove();
      });
      foregroundMessages$ = nativeForegroundMessages$;

      notificationClicks$ = new Rx.Observable(observer => {
        const pushNotificationsListener = PushNotifications.addListener(
          'pushNotificationActionPerformed',
          (actionPerformed: PushNotificationActionPerformed) => {
            observer.next({
              action: actionPerformed.actionId,
              notification: {
                type: 'push',
                data: actionPerformed.notification.data,
              },
            });
          },
        );

        const localNotificationsListener = LocalNotifications.addListener(
          'localNotificationActionPerformed',
          (actionPerformed: LocalNotificationActionPerformed) => {
            observer.next({
              action: actionPerformed.actionId,
              notification: {
                type: 'local',
                data: actionPerformed.notification.extra.data,
              },
            });
          },
        );

        return () => {
          pushNotificationsListener.remove();
          localNotificationsListener.remove();
        };
      });
    } else {
      this.tokenChanges$ = this.afMessaging.requestToken.pipe(
        map(token => token ?? undefined),
      );

      foregroundMessages$ = this.afMessaging.messages;

      notificationClicks$ = new Rx.Observable(observer => {
        const handler = (event: any) => {
          const internalPayload = event.data;

          if (!internalPayload.firebaseMessaging) {
            return;
          }

          if (
            internalPayload.firebaseMessaging.type === 'notification-clicked' &&
            internalPayload.firebaseMessaging.payload
          ) {
            observer.next({
              // TODO: T267853 - implement custom actions when needed
              notification: {
                ...internalPayload.firebaseMessaging.payload.notification,
                data: internalPayload.firebaseMessaging.payload.data,
              },
            });
          }
        };
        navigator.serviceWorker.addEventListener('message', handler);
        return () => {
          navigator.serviceWorker.removeEventListener('message', handler);
        };
      });
    }

    const result = await firstValueFrom(this.tokenChanges$)
      .then(() => ({
        status: 'success' as const,
      }))
      .catch(e => ({
        status: 'error' as const,
        errorCode: NotificationPermissionErrorCode.TOKEN_REQUEST_ERROR,
        // T249142: Required to capture what the problem we're having with
        // some users not registering push notifications correctly.
        errorMsg: e.toString(),
      }));

    // Permission has been granted by the user
    if (result.status === 'success') {
      const deviceId = await this.getDeviceId();

      this.isRegistered = true;

      this.subscriptions.add(
        this.tokenChanges$
          .pipe(
            tap(token => console.log('Device token:', token)),
            switchMap(token =>
              this.auth.getUserId().pipe(
                take(1),
                switchMap(userId =>
                  // The token is automatically updated based on the unique device ID
                  this.freelancerHttp.post('users/0.1/tokens', {
                    kind: 'fcm',
                    app_type: 'freelancer',
                    data: token,
                    device_id: deviceId,
                    user_id: toNumber(userId),
                  }),
                ),
              ),
            ),
          )
          .subscribe({
            error: err => {
              this.errorHandler.handleError(err);
            },
          }),
      );

      this.subscriptions.add(
        foregroundMessages$.subscribe(notification => {
          // This is called when notifications are received while the app is in
          // foreground. Presumably we don't need to do anything here for
          // non-native environment as the same notifications will be received
          // through the WebSocket, which will be used to update the UI(pops up
          // the messaging thread) or show a toast notification.
          console.log('New foreground message: ', notification);
        }),
      );

      // New messages that comes through WebSocket doesn't pops up in the UI on
      // Android and iOS. Hence, we create local notifications to alert user.
      if (this.pwa.isNative() && nativeForegroundMessages$) {
        this.subscriptions.add(
          nativeForegroundMessages$.subscribe(async notification => {
            if (notification.body && notification.data.url) {
              // Strip the query params from the relative URL in the notification.
              const redirectURL = notification.data.url.replace(/\?.*$/, '');
              // Ignore notification if we are already in the message thread.
              if (redirectURL !== this.location.pathname) {
                // Forward the push notification to local notification.
                LocalNotifications.schedule({
                  notifications: [
                    {
                      // Generate a random ID.
                      id: Math.floor((Math.random() + 1) * 0x10000000),
                      title: notification.title ?? $localize`New Message`,
                      body: notification.body,
                      // This set the sound file for iOS.
                      // Android sound file is set via the channel.
                      sound: 'message.wav',
                      channelId: 'newMessageChannel',
                      extra: {
                        data: notification.data,
                      },
                    },
                  ],
                });
              }
            }
          }),
        );
      }

      this.subscriptions.add(
        notificationClicks$.subscribe(({ action, notification }) => {
          this.trackClickedNotification(notification);

          // This is called when the user performs an action on a notification
          console.log('Notification click: ', action, notification);
          // If the url data field is set, navigate to the destination
          const url = notification?.data?.url;

          // This is to avoid reloading the page after the PWA loader has
          // already navigated to the correct route.
          if (url && url !== this.location.href) {
            this.location.navigateByUrl(url, { replaceUrl: false });
          }
        }),
      );
    }

    return result;
  }

  /**
   * Unregister the application to receive push notifications.
   */
  unregisterPushNotifications(): Promise<void> {
    // Reset subscriptions
    this.subscriptions.unsubscribe();
    this.subscriptions = new Rx.Subscription();
    this.isRegistered = false;

    // Silently return if notifications haven't been setup
    if (!this.tokenChanges$) {
      return Promise.resolve();
    }

    return firstValueFrom(
      this.tokenChanges$.pipe(
        switchMap(token => {
          if (token) {
            return Rx.combineLatest([
              this.freelancerHttp.delete('users/0.1/tokens', {
                params: {
                  kind: 'fcm',
                  app_type: 'freelancer',
                  data: token,
                },
              }),
              // if not native, delete token
              !this.pwa.isNative()
                ? this.afMessaging.deleteToken(token)
                : // otherwise ignore
                  Rx.of(undefined),
            ]);
          }
          return Rx.EMPTY;
        }),

        map(() => undefined),
      ),
    );
  }

  /**
   * @deprecated
   * Manually trigger a notification
   */
  showNotification() {
    // We explicitly DO NOT want to implement that, i.e. create & show
    // Notifications from the webapp itself: all notifications should be
    // transformed/filtered/sent from the backend and are automatically handled
    // in the Service Worker/native background tasks in order to ensure
    // consistency & provide background notifications support accross platforms
    throw Error('Not implemented on purpose');
  }

  /**
   * Remove notifications when user switches back to the app
   */
  async removeNotificationsOnActiveState(): Promise<void> {
    if (this.pwa.isNative()) {
      // Clear notifications on fresh open of the app
      await App.getState().then(async (state: AppState) => {
        if (state.isActive) {
          await this.trackDeliveredNotifications();
          PushNotifications.removeAllDeliveredNotifications();
        }
      });
    }
  }

  /*
   * Send tracking for delivered notifications.
   *
   * Note: does not include the notification that was clicked on
   */
  private async trackDeliveredNotifications() {
    const deliveredNotifications = await PushNotifications.getDeliveredNotifications();
    deliveredNotifications.notifications.forEach(
      (notification: PushNotificationSchema) => {
        // Filter out the extra `group_summary` notification that is returned
        // here but is not actually a delivered push notification.
        if (!notification.groupSummary) {
          const extraParams: PushNotificationQtsExtraParams = {};

          this.tracking.trackCustomEvent(
            TRACK_DELIVERED_NOTIF_EVENT_NAME,
            TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
            extraParams,
          );
        }
      },
    );
  }

  /*
   * Send tracking for a clicked notifications.
   *
   * Since `trackDeliveredNotifications()` does not include the clicked notification,
   * this function will also send a delivered tracking for this notification
   */
  private async trackClickedNotification(notification: Notification) {
    const extraParams: PushNotificationQtsExtraParams = {};

    extraParams.clicked = 'true';

    if (notification.data?.url) {
      extraParams.url = notification.data?.url;
    }

    if (notification.data?.push_notification_type) {
      extraParams.pushNotificationType =
        notification.data.push_notification_type;
    }

    if (notification.data?.resource_id) {
      extraParams.resourceId = notification.data.resource_id;
    }

    this.tracking.trackCustomEvent(
      TRACK_CLICKED_NOTIF_EVENT_NAME,
      TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
      extraParams,
    );
    this.tracking.trackCustomEvent(
      TRACK_DELIVERED_NOTIF_EVENT_NAME,
      TRACK_NATIVE_PUSH_NOTIF_SECTION_NAME,
    );
  }

  private async getPermission(): Promise<NotificationPermission> {
    if (this.pwa.isNative()) {
      const result = await PushNotifications.checkPermissions();
      if (
        result.receive === 'prompt' ||
        result.receive === 'prompt-with-rationale'
      ) {
        return 'default';
      }
      return result.receive;
    }
    return Notification.permission;
  }

  private async getDeviceId(): Promise<string> {
    const deviceId = firstValueFrom(this.localStorage.get('deviceId'));
    if (typeof deviceId === 'string' && deviceId) {
      return deviceId;
    }
    const id = uuidv4();
    await this.localStorage.set('deviceId', id);
    return id;
  }
}
